import { QueryKey } from '@tanstack/react-query';
import type { MemberTimeEntryDataMember } from 'components/domain/time-entry/TimeEntryDataReport/MemberTimeEntryDataReport/types/types';
import type { ITimeEntryDataTableRowGetRowsOptions } from 'components/domain/time-entry/TimeEntryDataTable/hooks/useTimeEntryDataTableRow';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import ITimeRange from 'types/TimeRange';
import ITimeEntryReportWithHeaderData from '../../types/TimeEntryReportWithHeaderData';
import { IMemberTimeEntryFilter } from './useMemberTimeData';
import useMemberTimeEntryDataRows from './useMemberTimeEntryDataRows';

// TODO: probably consolidate this type to one type but they're both used in different parts of the app
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IMemberTimeEntryReportDataFilter extends IMemberTimeEntryFilter {}

export interface ITimeEntryDataReportMemberData extends ITimeEntryReportWithHeaderData {
  canManage: boolean;
  canManageTimeOff: boolean;
  isArchived: boolean;
}

function getMemberPageSize(timeRange: ITimeRange<DateTime>): number {
  const days = timeRange.endTime.diff(timeRange.startTime, 'days').days;

  if (days > 60) {
    return 2;
  }

  if (days > 24) {
    return 3;
  }

  if (days > 18) {
    return 5;
  }

  if (days > 12) {
    return 7;
  }

  if (days > 6) {
    return 10;
  }

  return 15;
}

export default function useMemberTimeEntryDataReportData(
  scroller: HTMLElement | null,
  filter: IMemberTimeEntryReportDataFilter,
  queryKey: QueryKey,
  customGetRowsOptions?: Partial<ITimeEntryDataTableRowGetRowsOptions<MemberTimeEntryDataMember>>
) {
  const getData = useMemberTimeEntryDataRows(filter, customGetRowsOptions);
  const pageSize = getMemberPageSize(filter.timeRange);

  const { data, loadAll, loadedAll, isLoading, isError, refetch } = useReactQueryLazyLoading(
    scroller,
    queryKey,
    getData,
    pageSize
  );

  return useMemo(
    () => ({
      data,
      loading: isLoading,
      loadedAll,
      onDataChange: refetch,
      error: isError,
      loadAll,
      clearData: refetch,
    }),
    [data, isLoading, isError, loadedAll, loadAll, refetch]
  );
}
